import Vue from "vue";
import VueRouter, { NavigationGuard, RawLocation, Location, Route, RouteConfig } from "vue-router";

import Shared from "@/shared";
import PortalComponent from "../views/Portal.vue";
import WelcomeComponent from "../views/WelcomeComponent.vue";
import Browse from "../views/Browse.vue";
import Apps from "../views/Apps.vue";
import FillOut from "../views/FillOut.vue";
import PageNotFound from "../views/PageNotFound.vue";
import Settings from "../views/Settings.vue";
import Subscriptions from "../views/Subscriptions.vue";
import Tasks from "../views/Tasks.vue";
import Document from "../views/Document.vue";
import Team from "../views/Teams/Team.vue";
import HandleTodo from "../views/HandleTodo.vue";
import AddTeam from "../views/Teams/AddTeam.vue";
import ReadList from "../views/ReadList.vue";
import AISearch from "@/views/AISearch.vue";
import AIBrowse from "@/views/AIBrowse.vue";
import storeHelper from "@/store/store-helper";
import Utils from "@/utils";
import { Controllers, Portal, settingName, UserGroupFilter } from "@infoland/iprova-api";
import { cookies as cookiesEnum } from "@/enums";
import hyperlinkResolver from "@/hyperlinkResolver";
import checkDocumentResolver from "@/checkDocumentResolver";
import { ErrorHandler } from "vue-router/types/router";
import campaigns from "@/campaigns";
import reviewDocumentResolver from "@/helpers/reviewDocumentResolver";
import officeOnlineFillableForm from "../views/OfficeOnlineFillableForm.vue";
import { MinimalUserInfo } from "@/store/helpers/user-store-helper";
import UserGroupApi from "@/services/usergroup";
import { UserGroupFilterPostDto } from "@/api/UserGroupFilterPostDto";
import { Constants } from "@/constants";
import VueCompositionAPI from "@vue/composition-api";
import * as UserApplicationState from "@/userapplicationstate";

Vue.use(VueCompositionAPI); //use composition api so that we can use vue 3 composition api in vue 2 components

export type RouteMeta = {
    allowAnonymous?: boolean | ((route: Route) => Promise<boolean>);
    campaignId?: string;
    fixAnonymousTodoLink?: boolean;
};

// TC (11/02/2021) Added this temporary fix because a bug exists in vue router where a redirected or aborted navigation error was shown in the console.
// This will be fixed in a future update of the router npm package, so this fix can be reconsidered in the future
const originalPush = VueRouter.prototype.push;
function augmentedPush(location: RawLocation): Promise<Route>;
function augmentedPush(location: RawLocation, onResolve?: Function, onReject?: ErrorHandler): void;
function augmentedPush(this: VueRouter, location: RawLocation, onResolve?: Function, onReject?: ErrorHandler): void | Promise<Route> {
    const boundOriginalPush = originalPush.bind(this);
    if (onResolve || onReject) {
        return boundOriginalPush(location, onResolve, onReject);
    } else {
        return boundOriginalPush(location).catch(err => {
            if (VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.redirected)) {
                //Only resolve if error is of type redirected
                return Promise.resolve(err.from);
            }
            if (VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.aborted) && (location as Location).name == "hyperlink") {
                //Only resolve if error is of type redirected
                return Promise.resolve(err.from);
            }
            // rethrow error
            return Promise.reject(err);
        });
    }
}

VueRouter.prototype.push = augmentedPush;

Vue.use(VueRouter);

const WaitForData = async <T>(getter: () => T, validator: (data: T) => boolean = undefined, interval: number = 50): Promise<T> => {
    let data: T;
    //wait for data to be something (not null) or optionally for validator to return true
    while ((data = getter()) === null || (validator !== undefined && validator(data) === false)) {
        await new Promise(resolve => {
            setTimeout(resolve, interval);
        });
    }

    return data;
};

const WaitForPortals = function (): Promise<Portal[]> {
    //wait for portals in store to become something
    return WaitForData<Portal[]>(() => storeHelper.portalStore.portals);
};

//navigation guards
const navigationGuards: Record<string, NavigationGuard<Vue>> = {
    //[DF-64358] Fix incorect link for handle todo as external actor
    fixAnonymousTodoLink(to: Route, from, next) {
        const meta: RouteMeta = to.meta;
        if (meta.fixAnonymousTodoLink) {
            if (to && to.query["ExternalUserUniqueKey"] !== undefined) {
                const todoId = to.params["todoId"] ? to.params["todoId"] : -1;
                const path = Shared.GetAuthenticatedUrl(
                    `${Shared.iProvaUrl}/iTask/Default.aspx?TodoID=${todoId}&ExternalUserUniqueKey=${to.query["ExternalUserUniqueKey"]}`
                ).toString();
                window.open(path, "_self");
            } else {
                next();
            }
        } else {
            next();
        }
    },
    async checkLoginRequired(to: Route, from, next) {
        const meta: RouteMeta = to.meta;
        let isAuthRequired: boolean;

        if (typeof meta.allowAnonymous == "function") {
            isAuthRequired = (await meta.allowAnonymous(to)) !== true;
        } else {
            isAuthRequired = meta.allowAnonymous !== true;
        }

        if (isAuthRequired) {
            //loaded user is anonymous?
            const user: MinimalUserInfo = (await storeHelper.userStore.getLoggedOnUser()) as MinimalUserInfo;
            if (Utils.isAnonymousUser(user.user_id)) {
                //open login
                const path = Utils.getCookie(cookiesEnum.iProva_LoginRedirect) ? undefined : `${Shared.iProvaUrl}/portal/#${to.path}`;
                Utils.navigateToLogin(path);
                return next(false); // abort
            }
            //not anonymous => continue
        }

        // if you got here, you don't need this cookie anymore
        Utils.deleteCookie(cookiesEnum.iProva_LoginRedirect);

        //continue
        next();
    },
    setCampaignId(to, from, next) {
        /**
         * Puts campaignId in to-route meta info when to.name has a campaign
         */
        (to.meta as RouteMeta).campaignId = campaigns[to.name];

        // continue
        next();
    },
    //[DF-65722] Fix sometimes opening the portal welcome screen gives error
    //This is because portals are sometimes not initialized before the app loads all portals which is a loadRequiredData
    async ensurePortalsAreLoaded(to, from, next) {
        const portals = await WaitForPortals();
        if (!portals) return next(false);

        // continue
        next();
    }
};

/***
 * find the route to go to as start
 */
const resolveStart = async (from, to, next) => {
    //determine start route
    let startPortalId: number;
    const user: MinimalUserInfo = (await storeHelper.userStore.getLoggedOnUser()) as MinimalUserInfo;
    const portals = await WaitForPortals();
    if (Utils.isAnonymousUser(user.user_id)) {
        //are there any portals?
        if (portals.length == 0) {
            //anonymous user without any portals
            startPortalId = null;
        } else if (portals.length == 1) {
            //use the one and only as start portal
            startPortalId = portals[0].portal_id;
        } else {
            //get portal from cookie
            const cookieStartPortal = Utils.getCookie(cookiesEnum.StartPortal);
            //check value
            if (isNaN(cookieStartPortal)) cookieStartPortal == 0;
            else startPortalId = cookieStartPortal;
        }
    } else {
        const authUser = await storeHelper.userStore.getLoggedOnUser();
        //not anonymous, get start portal from user
        startPortalId = authUser.start_portal?.portal_id;

        //if no user portal, check if user has a group that has a start portal
        if (portals.length && !startPortalId && authUser.user_groups) {
            //get user's groups (limit by 100)
            const userGroupIds = authUser.user_groups.map(x => x.user_group_id);
            const groupFilter: UserGroupFilterPostDto = { user_group_ids: userGroupIds };
            const userGroups = (await UserGroupApi.getUserGroupsAsync(groupFilter, 0, 100)).items;

            //find start portal in groups
            for (const userGroup of userGroups) {
                //check if group has startportal and if user has access to it
                if (userGroup.start_portal && portals.some(x => x.portal_id == userGroup.start_portal.portal_id)) {
                    startPortalId = userGroup.start_portal.portal_id;
                    break;
                }
            }
        }
    }

    if (startPortalId > 0) {
        //go to portal
        next({ name: "Portal", params: { portalId: startPortalId.toString() } });
    } else {
        next({ name: "Welcome" });
    }
};

const checkCanGoBack = (to, from, next) => {
    const fromRoute = from as Route;
    let canGoBack = false;

    if (fromRoute && fromRoute.name) canGoBack = true;

    //Store can go back
    storeHelper.siteStore.setCanGoBack(canGoBack);

    next();
};

const checkDocument = async (to, from, next) => {
    const redirected = await checkDocumentResolver(to, from, next);
    if (!redirected) {
        checkCanGoBack(to, from, next);
    }
};
const checkCanGoReview = async (to, from, next) => {
    const isRedirected = await reviewDocumentResolver(to, from, next);
    if (!isRedirected) {
        const fromRoute = from as Route;
        let canGoBack = false;
        if (fromRoute && fromRoute.name) canGoBack = true;
        //Store can go back
        storeHelper.siteStore.setCanGoBack(canGoBack);
    }
    next();
};

// routes
const routes: RouteConfig[] = [
    {
        path: "/portal",
        redirect: "/" //resolves start portal
        //Navigation guards are not applied on redirects:  https://router.vuejs.org/guide/essentials/redirect-and-alias.html#redirect
    },
    {
        path: "/portal/:portalId",
        name: "Portal",
        component: PortalComponent,
        props: route => ({
            portalId: parseInt(route.params.portalId)
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/portal/:portalId/browse",
        name: "Browse",
        component: Browse,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            searchText: route.query.searchText,
            scope: "portal",
            title: route.query.title
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/portal/:portalId/search",
        name: "Search",
        component: Browse,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            searchText: route.query.searchText,
            scope: route.query.scope || "portal",
            title: route.query.title
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    // new browse and AI Search route for AI
    {
        path: "/search/:portalId",
        name: "AISearch",
        component: AISearch,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            searchText: route.query.searchText,
            collectionId: parseInt(route.query.collectionId as string) || null
        })
    },
    {
        path: "/browse/:portalId",
        name: "AIBrowse",
        component: AIBrowse,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            collectionId: parseInt(route.query.collectionId as string) || null
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/collections/:collectionIds",
        name: "Collections",
        component: Browse,
        props: route => ({
            collectionIds: route.params.collectionIds.split(",").map(i => {
                return parseInt(i);
            }),
            settingsPortalId: route.query.portalId ? parseInt(route.query.portalId as string) : null,
            searchText: route.query.searchText,
            scope: "global",
            title: route.query.title
        }),
        meta: {
            allowAnonymous: async (to: Route): Promise<boolean> => {
                //bugfix TT64393, if SSO is enabled do not allow anonymous.
                const autoLoginValue = await storeHelper.settingsStore.getSettingAsync(settingName.autologin);
                return autoLoginValue == "none";
            }
        } as RouteMeta
    },
    {
        path: "/portal/:portalId/fillout",
        name: "FillOut",
        component: FillOut,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            searchText: route.query.searchText
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        name: "hyperlink",
        path: "/hyperlink/:hyperlink_id",
        beforeEnter: hyperlinkResolver,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/tasks",
        name: "Tasks",
        redirect: () => {
            let lastTab: string | null = sessionStorage.getItem(Constants.Task.ApplicationsState.TasksLastTab);
            if (lastTab == null) lastTab = "tasks";

            return `/tasks/${lastTab}`;
        }
    },
    {
        path: "/tasks/:tab",
        name: "Tasks",
        component: Tasks,
        props: route => ({
            tab: route.params.tab,
            filter: route.query.filter
        })
    },
    {
        path: "/portal/:portalId/tasks/:tab",
        name: "Tasks",
        component: Tasks,
        props: route => ({
            portalId: parseInt(route.params.portalId),
            tab: route.params.tab
        })
    },
    {
        path: "/portal/:portalId/dashboard",
        name: "Dashboard",
        props: route => ({
            portalId: parseInt(route.params.portalId)
        }),
        component: () => import(/* webpackChunkName: "Dashboard" */ "../views/Dashboard/Dashboard.vue")
    },
    {
        path: "/welcome",
        name: "Welcome",
        component: WelcomeComponent,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/apps",
        name: "Apps",
        component: Apps
    },
    {
        path: "/team/add",
        name: "AddTeam",
        component: AddTeam
    },
    {
        path: "/team/:teamId/",
        name: "Team",
        redirect: "/team/:teamId/tasks"
    },
    {
        path: "/team/:teamId/:tab",
        name: "Team",
        component: Team,
        props: route => ({
            tab: route.params.tab,
            teamId: route.params.teamId
        })
    },
    {
        path: "/notfound",
        name: "PageNotFound",
        component: PageNotFound,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/settings",
        name: "Settings",
        component: Settings
    },
    {
        path: "/subscriptions",
        name: "Subscriptions",
        component: Subscriptions
    },
    {
        path: "/document/:documentId",
        name: "Document",
        component: Document,
        props: route => ({
            documentId: route.params.documentId,
            bookmark: route.query.bookmarkid,
            paragraph: route.query.paragraphid ? parseInt(route.query.paragraphid.toString()) : -1,
            page: route.query.pagenumber ? parseInt(route.query.pagenumber.toString()) : -1,
            from: route.query.from
        }),
        beforeEnter: checkCanGoBack,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/document/:documentId/check",
        name: "CheckDocument",
        component: Document,
        props: route => ({
            documentId: route.params.documentId
        }),
        beforeEnter: checkDocument
    },
    {
        path: "/document/:documentId/review",
        name: "DocumentReview",
        component: Document,
        props: route => ({
            documentId: route.params.documentId
        }),
        beforeEnter: checkCanGoReview
    },
    {
        path: "/document/:documentId/read",
        name: "ReadDocument",
        component: Document,
        props: route => ({
            documentId: route.params.documentId
        }),
        beforeEnter: checkCanGoBack,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/document/:documentId/:versionNumber/preview",
        name: "PreviewDocument",
        component: Document,
        props: route => ({
            documentId: route.params.documentId,
            versionNumber: parseInt(route.params.versionNumber.toString())
        }),
        beforeEnter: checkCanGoBack,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/todo/:todoId",
        name: "HandleTodo",
        component: HandleTodo,
        props: route => ({
            todoId: parseInt(route.params.todoId)
        }),
        meta: {
            allowAnonymous: route => Promise.resolve(!!route.query.ExternalUserUniqueKey),
            fixAnonymousTodoLink: true
        } as RouteMeta
    },
    {
        path: "/qc/:quickCode",
        name: "QuickCode",
        component: Document,
        props: route => ({
            quickCode: route.params.quickCode
        }),
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/",
        name: "start",
        beforeEnter: resolveStart,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/TA/campaign",
        name: "TA_Campaign",
        component: WelcomeComponent,
        meta: {
            allowAnonymous: true
        } as RouteMeta
    },
    {
        path: "/readlist",
        name: "ReadList",
        component: ReadList,
        props: route => ({
            fromReadNextDocument: route.params.fromReadNextDocument
        }),
        meta: {
            fixAnonymousTodoLink: true
        } as RouteMeta
    },
    {
        path: "/officeOnline/fillableForm",
        name: "OfficeOnlineFillableForm",
        component: officeOnlineFillableForm
    },
    {
        path: "*",
        redirect: { name: "PageNotFound" }
    }
];

const router = new VueRouter({
    routes
});

//ensure initial data in App.vue to be loaded before navigating to components
router.beforeEach(navigationGuards.fixAnonymousTodoLink);
router.beforeEach(navigationGuards.checkLoginRequired);
router.beforeEach(navigationGuards.setCampaignId);
router.beforeEach(navigationGuards.ensurePortalsAreLoaded);

export default router;
